import { ol_Feature, ol_FeatureLike, ol_style, ol_geom } from "@omnia/exports/openlayers"
import { IHealthySoilsAssessment } from "../../hsa/types"
import { datalayers, DatalayersMap, FieldViewModel, IDatalayerMapDependencies, IOmniaSettings, isObject, isTerraMapLayer, MapSelectInteraction3, selectMany, SoilMapView } from "../../omnia"
import { IDataLayer, ILayerViewModel, ISoilService, LayerZone } from "../../types"
import { getLayerZoneId } from "../utils"
import { AnalysisLayer } from "./analysis-layer"
import { createMapViewGroup, MapViewGroup } from "./group"
import { IViewerContext } from "./types"
import { HsaLayer } from "./view/hsa"
import { createNitrogenGroup } from "./view/nitrogen"
import { createPcnAnalysisGroup } from "./view/pcn"
import { createSoilAnalysisGroup } from "./view/std"
import { createTerraMapGroup } from "./view/terramap"

export const groupMapFeaturesInSingleLayer = false

export class SoilMapContext {

    private _settings: IOmniaSettings

    private _map: DatalayersMap

    private _viewGroups: MapViewGroup[] = []
    private _allViews: SoilMapView[]
    private _currentGroup: MapViewGroup
    private _currentView: SoilMapView

    private _analysisLayer: AnalysisLayer

    private _showHsa: boolean
    private _hsaLayers: ILayerViewModel[]

    private _showTerraMap: boolean


    constructor(
        private soilSvc: ISoilService,
        private layers: IDataLayer[],
        private mapDependencies: IDatalayerMapDependencies) {

        this._settings = soilSvc.settings.getSettings("map")

        this._showTerraMap = layers.some(isTerraMapLayer)

        this.setupMap()

        this.setupViews()

    }

    dispose() {

        if (this._map) {
            this._map.dispose()
            this._map = null
        }

    }

    private setupMap() {

        this._map = new DatalayersMap(this.mapDependencies, {
            selecInteractionFactory: m => new MapSelectInteraction3(m.getMap())
        })

        this._map.enableMultipleSelection = false

        this._map.setSelectedStyleProvider((feature, resolution) => this.getSelectedStyle(feature, resolution))

        if (groupMapFeaturesInSingleLayer) {
            this._analysisLayer = new AnalysisLayer()
            this._map.addLayer(this._analysisLayer, true)
        }

    }

    refreshViews(layers: IDataLayer[]) {

        if (this._allViews) {
            this._allViews.forEach(v => v.dispose())
        }

        this.layers = layers

        this.setupViews()

    }

    refreshCurrentView() {
        if (this._currentView) {
            this.updateMap(this._currentView, null)
        }
    }

    private setupViews() {

        const datalayerContext = this.mapDependencies.createContext(() => {

            return this._currentView
                ? this._currentView.getLayers().map(l => l.mapLayer).filter(l => !!l)
                : this._map.getDataLayers()

        })

        let viewerContext: IViewerContext = {

            soilSvc: this.soilSvc,

            map: this._map,
            datalayerContext: datalayerContext,
            viewManager: this.mapDependencies.viewManager,

            layers: this.layers,

            createLayerViewModel: (l, f, o) => new datalayers.LayerViewModel(datalayerContext, this._map, l, f, o)

        }

        this._viewGroups = [

            createMapViewGroup("C", "Cropping", [new SoilMapView("Crops")]),

            createNitrogenGroup(viewerContext),

            this._showTerraMap
                ? createTerraMapGroup(viewerContext)
                : createSoilAnalysisGroup(viewerContext),

            createPcnAnalysisGroup(viewerContext),


        ].filter(g => !!g)

        this._allViews = selectMany(this._viewGroups.map(g => g.views))

        this.setupHsaLayer(viewerContext)

        this.restoreSettings()

    }

    private setupHsaLayer(viewerContext: IViewerContext) {

        if (this._hsaLayers) {
            this._hsaLayers.forEach(l => l.dispose())
        }

        let hsa = this.soilSvc.getHealthySoilsAssessments()

        this._hsaLayers = HsaLayer.createVirtualLayers(viewerContext, hsa)

        this.setHsaVisibility(this._showHsa)

    }

    private restoreSettings() {

        let view: { group: string, view: string } = this._settings.get("view")

        const ok = isObject(view)
            ? this.selectView(view.group, view.view)
            : false

        if (!ok) {
            this.selectView("C")
        }

    }


    private saveSettings() {

        let group = this._currentGroup
        let view = this._currentView

        this._settings.set("view", group && view ? { group: group.key, view: view.key } : undefined)

    }

    get map(): DatalayersMap {
        return this._map
    }

    get showTerraMap() {
        return this._showTerraMap
    }

    set showTerraMap(value) {
        this._showTerraMap = value
        this.refreshViews(this.layers)
        this.map.clearSelection()
    }

    watchSelection(watcher: (features: ol_Feature[]) => void): Function {
        return this._map.watchSelection(watcher)
    }

    updateMapSelection(target: any) {

        if (target instanceof ol_Feature) {

            this._map.selectFeatures([target])

        }
        else {

            let f = this._map.findFeaturesWithData([target])

            this._map.selectFeatures(f)

            return (f && (f.length > 0))

        }

    }

    zoom(dataItem: any) {
        this._map.viewExtentForDataItem(dataItem)
    }

    viewDefaultExtent() {
        this._map.viewDefaultExtent()
    }

    get allViews(): SoilMapView[] {
        return this._allViews
    }

    get viewGroups(): MapViewGroup[] {
        return this._viewGroups
    }

    get group(): MapViewGroup {
        return this._currentGroup
    }

    set group(value: MapViewGroup) {
        if (value != this._currentGroup) {
            this._currentGroup = value
            this.view = value.currentView
            this._map.clearSelection()
        }
    }

    get view(): SoilMapView {
        return this._currentView
    }

    set view(value: SoilMapView) {
        if (this._currentView != value) {
            let previousView = this._currentView
            this._currentView = value
            this._currentGroup = this.viewGroups.find(g => g.views.some(v => v == value))
            this.group.currentView = value
            this.updateMap(value, previousView)
            this.saveSettings()
        }
    }

    get colorKey() {
        return this.view?.getColorKey()
    }


    findZoneInCurrentView(zone: LayerZone): LayerZone {

        const features = this._currentView?.findFeature(getLayerZoneId(zone))

        if (features && (features.length == 1)) {
            const data = features[0].get("data")
            return (data) ? data : undefined
        }

    }

    get showHsa() {
        return this._showHsa
    }

    set showHsa(value: boolean) {
        this._showHsa = value
        this.setHsaVisibility(value)
    }

    private updateMap(view: SoilMapView, previousView: SoilMapView) {

        if (previousView) {
            previousView.hideLayers()
        }

        if (view) {
            view.showLayers(isLayerVisible)
        }

        this.updateMapLabels(view)

        this.setHsaVisibility(this._showHsa)

    }

    private updateMapLabels(view: SoilMapView) {

        let hidden: { field: FieldViewModel, cropping: FieldViewModel }[] = []

        if (view) {

            let layers = view.getLayers()

            hidden = layers.filter(l => l.mapLayer && l.mapLayer.zones && (l.mapLayer.zones.length > 0)).map(l => {
                return {
                    field: l.field,
                    cropping: l.cropping
                }
            })

        }

        this.setHiddenLabels(hidden)

    }

    private setHsaVisibility(visible: boolean) {
        if (this._hsaLayers) {
            this._hsaLayers.forEach(l => l.visible = visible)
        }
    }

    private setHiddenLabels(hidden: { field: FieldViewModel, cropping: FieldViewModel }[]) {

        [this.map.croppingLayer, this.map.fieldsLayer].forEach(l => {

            l.getFeatures().forEach(f => {
                let cropping: FieldViewModel = f.get("data")
                let h = hidden.some(h => (h.cropping == cropping) || (h.field == cropping.fieldVm))
                f.set("hideLabel", h)
            })

            l.refresh()

        })

    }


    selectView(group: string, element?: string) {

        const g = this.viewGroups.find(g => g.key == group)
        const view = g ? g.views.find(v => v.key == element) : null

        if (view) {
            this.view = view
            return view
        }

    }

    getZoneFromCurrentView(zoneId: number) {

        if (!this._currentView) return

        let layers = this._currentView.getLayers()

        let zones = layers.map(l => l.getZone(zoneId)).filter(z => !!z)

        if (zones.length) {
            return zones[0]
        }

    }

    getCroppingZoneFromCurrentView(croppingId: number) {

        if (!this._currentView) return

        let layers = this._currentView.getLayers()

        let layer = layers.find(l => l.datalayer.CroppingId == croppingId)

        if (layer && layer.mapLayer && layer.mapLayer.zones && layer.mapLayer.zones.length) {
            return layer.mapLayer.zones[0]
        }

    }

    selectHsaZone(hsaId: number) {

        if (!this._hsaLayers) return

        this.map.clearSelection()

        if (!hsaId) return

        if (!this.showHsa) {
            this.showHsa = true
        }

        this._hsaLayers.forEach(vm => {

            vm.mapLayer.zones.forEach(z => {

                let hsa = <IHealthySoilsAssessment>z.zone.Data

                if (hsa.HealthySoilsAssessmentId == hsaId) {

                    let feature = this.map.findFeatureWithData(z)

                    if (feature) {
                        this.map.selectFeatures([feature])
                        this.focusOnHsa(hsa, feature)
                        return true
                    }

                }

            })

        })

    }

    private focusOnHsa(hsa: IHealthySoilsAssessment, feature: ol_Feature) {

        let point = <ol_geom.Point>feature.getGeometry()
        let coords = point.getCoordinates()

        if (hsa.Allocations) {

            let fields = hsa.Allocations.map(a => this.soilSvc.findField(a.FieldId))

            if (fields.length) {
                this.map.viewExtentForDataItems(fields)
            }

        }

        this.map.setCenter(coords)

    }


    refresh() {

        this.map.refresh()
        this.map.getLayers().forEach(l => l.refresh())

    }

    private getSelectedStyle(feature: ol_FeatureLike, resolution: number): ol_style.Style[] {

        let data = feature.get("data")

        if (data instanceof datalayers.DrawingFeature) {
            return data.getZonePointStyle(true, resolution)
        }

        return null
    }

}

export function isLayerVisible(l: ILayerViewModel) {

    const cropping = l.cropping
    if (cropping) return cropping.visible

    const field = l.field
    if (field) return field.visible

    return true

}
